import { chain, get, last, sumBy } from "lodash"
import { generateGathererKeys } from "connected-components/dashboard/helpers/query-key"
import { isUnmodeled } from "helpers/securities"
import { buildContributorKey } from "helpers/contributors"
import { getFactorValueByMetricDisplay } from "./helpers/factor-utils"
import { getContributorLabel, NOT_HELD } from "../../connected-components/asset-view/helpers"
import { getGroupByQueryParameters, getGroupBySubqueryParameters, getGroupByFields, sanitizePositionSet, getIsNoLongerHeldFromContributor, CUMULATIVE_RETURNS_SUBQUERY, isIntradaySimulationEligible } from "../helpers"
import { getResidualReturnSubquery, getResidualAttributionSummarySubquery } from "helpers/residualAttribution"

function getAttributionSummarySubquery(isRfrAttributionEnabled) {
  return `
  summary {
    factorsReturn {
      ${CUMULATIVE_RETURNS_SUBQUERY}
    }
    specificReturn {
      ${CUMULATIVE_RETURNS_SUBQUERY}
    }
    tradingReturn {
      ${CUMULATIVE_RETURNS_SUBQUERY}
    }
    ${isRfrAttributionEnabled ? getResidualReturnSubquery(true) : ""}
  }`
}

const ATTRIBUTION_FACTORS_SUBQUERY = `
  factors {
    id
    name
    category
    return {
      ${CUMULATIVE_RETURNS_SUBQUERY}
    }
  }`

function getContributorsSubquery(isRfrAttributionEnabled) {
  return `
  id
  ticker
  mic
  description
  isUnmodeled
  modelProviderId
  assetClass
  notHeld
  country
  currency
  bloombergTicker
  sector
  averagePercentEquity
  totalReturn {
    ${CUMULATIVE_RETURNS_SUBQUERY}
  }
  attribution {
    ${getAttributionSummarySubquery(isRfrAttributionEnabled)}
    ${ATTRIBUTION_FACTORS_SUBQUERY}
  }
`
}

function getUngroupedContributorsSubquery(isRfrAttributionEnabled) {
  return `performanceContributors (includeNotHeld: $includeNotHeld) {
  ${getContributorsSubquery(isRfrAttributionEnabled)}
}`
}

function getGroupedContributorsSubQuery(groupByFields, isRfrAttributionEnabled) {
  return `
  groupedPerformanceContributors(${getGroupBySubqueryParameters(groupByFields)}, includeNotHeld: $includeNotHeld) {
    id
    name
    totalReturn {
      ${CUMULATIVE_RETURNS_SUBQUERY}
    }
    attribution {
      ${getAttributionSummarySubquery(isRfrAttributionEnabled)}
      ${ATTRIBUTION_FACTORS_SUBQUERY}
    }
    contributors {
      ${getContributorsSubquery(isRfrAttributionEnabled)}
    }
  }`
}

function getPerformanceSubquery(isRfrAttributionEnabled) {
  return `performance {
          totalReturn {
            ${CUMULATIVE_RETURNS_SUBQUERY}
          }
          attribution {
            summary {
              factors {
                ${CUMULATIVE_RETURNS_SUBQUERY}
              }
              specific {
                ${CUMULATIVE_RETURNS_SUBQUERY}
              }
              trading {
                ${CUMULATIVE_RETURNS_SUBQUERY}
              }
              ${isRfrAttributionEnabled ? getResidualAttributionSummarySubquery(true) : ""}
            }
            ${ATTRIBUTION_FACTORS_SUBQUERY}
          }
        }`
}

function getQuery(groupByFields, isRfrAttributionEnabled, shouldUseIntradayQuery = false) {
  const extraParameters = groupByFields ? getGroupByQueryParameters(groupByFields) : ""

  const contributorsSubquery = groupByFields
    ? getGroupedContributorsSubQuery(groupByFields, isRfrAttributionEnabled)
    : getUngroupedContributorsSubquery(isRfrAttributionEnabled)

  const performanceSubquery = getPerformanceSubquery(isRfrAttributionEnabled)

  // Live single-day intraday: performance from intradaySimulation, contributors from EOD.
  // intradaySimulationBatch carries no performance/performanceContributors fields (only risk,
  // exposure, beta, VaR, composition, factorSensitivity), so there is no batch-specific performance
  // query. Pinned historical batches intentionally stay on the standard EOD query.
  if (shouldUseIntradayQuery) {
    return `#performance-contributors
query PerformanceContributorsQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $from: Date!, $to: Date!, $includeNotHeld: Boolean!${extraParameters}) {
    model (id: $modelId) {
      intradayPerformance: intradaySimulation (positionSet: $positionSet, base: $base, on: $on) {
        ${performanceSubquery}
      }
      simulation (positionSet: $positionSet, base: $base, from: $from, to: $to, interval: END_DATE) {
        ${contributorsSubquery}
      }
    }
  }`
  }

  return `#performance-contributors
query PerformanceContributorsQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!, $includeNotHeld: Boolean!${extraParameters}) {
    model (id: $modelId) {
      simulation (positionSet: $positionSet, base: $base, from: $from, to: $to, interval: END_DATE) {
        ${performanceSubquery}
        ${contributorsSubquery}
      }
    }
  }`
}

export function getPerformanceReturns({ totalReturn, attribution, ...contributor } = {}, options = {}) {
  if (!totalReturn && !attribution) {
    return {}
  }

  const metricDisplay = options.selectedMetricDisplay
  const attributionFactors = attribution?.factors || []

  return {
    descriptors: contributor,
    totalReturn: getFactorValueByMetricDisplay(totalReturn, metricDisplay),
    ...getSummaryReturns(attribution?.summary, metricDisplay),
    ...getFactorReturns(attributionFactors, metricDisplay),
  }
}

export function getSummaryReturns(summary, metricDisplay) {
  if (!summary || typeof summary !== "object") {
    return {}
  }
  return Object.fromEntries(
    Object.entries(summary).map(([key, value]) => [
      key.includes("Return") ? key : `${key}Return`,
      getFactorValueByMetricDisplay(value, metricDisplay),
    ]),
  )
}

export function getFactorReturns(factors, metricDisplay) {
  if (!Array.isArray(factors)) {
    return {}
  }

  const individualReturns = chain(factors)
    .mapKeys(factor => factor?.id)
    .pickBy(factor => factor?.return)
    .mapValues(factor => getFactorValueByMetricDisplay(factor.return, metricDisplay))
    .value()

  const aggregatedReturns = chain(factors)
    .filter(factor => factor?.return && factor?.category)
    .groupBy("category")
    .mapValues(categoryFactors => {
      if (metricDisplay) {
        return sumBy(categoryFactors, factor => getFactorValueByMetricDisplay(factor.return, metricDisplay) ?? 0)
      }
      return categoryFactors.reduce((acc, factor) => {
        Object.entries(factor.return).forEach(([key, value]) => {
          acc[key] = (acc[key] ?? 0) + value
        })
        return acc
      }, {})
    })
    .value()

  return {
    ...individualReturns,
    ...aggregatedReturns,
  }
}

export function parseContributors(prefix, contributors, options = {}) {
  if (!Array.isArray(contributors)) {
    return {}
  }

  return contributors.reduce((acc, contributor) => {
    if (!contributor) {
      return acc
    }

    if (contributor.notHeld && options.notHeldRollup) {
      if (!acc[NOT_HELD]) {
        acc[NOT_HELD] = {
          label: "Not Held",
          notHeld: true,
          values: {
            [prefix]: {},
          },
        }
      }

      const performanceData = getPerformanceReturns(contributor, options)
      Object.entries(performanceData).forEach(([key, value]) => {
        if (typeof value === "number") {
          if (!acc[NOT_HELD].values[prefix][key]) {
            acc[NOT_HELD].values[prefix][key] = value
          } else {
            acc[NOT_HELD].values[prefix][key] += value
          }
        }
      })
    } else {
      acc[buildContributorKey(contributor)] = {
        label: getContributorLabel(contributor),
        notHeld: contributor.notHeld || false,
        isNoLongerHeld: getIsNoLongerHeldFromContributor(contributor),
        isUnmodeled: isUnmodeled(contributor),
        values: {
          [prefix]: getPerformanceReturns(contributor, options),
        },
      }
    }
    return acc
  }, {})
}

export function parseGroupedContributors(prefix, groups, options = {}) {
  if (!Array.isArray(groups)) {
    return {}
  }

  return chain(groups)
    .filter(group => group && group.id)
    .keyBy("id")
    .mapValues(group => {
      return {
        label: group.name || group.id,
        values: {
          [prefix]: getPerformanceReturns(group, options),
        },
        children: parseContributors(prefix, group.contributors, options),
      }
    })
    .value()
}

export function createPerformanceContributorsQuery({ prefix, modelId, positionSet, base, from, to, groupByOptions, ...options }) {
  const { shouldUseIntradayQuery: isIntradayMode, isRfrAttributionEnabled = false } = options
  // A batch matching the latest committed batch is a live view; only a genuinely pinned
  // historical batch stays on the EOD query, whose today row op-hops keys to that batch.
  const shouldUseIntradayQuery = isIntradaySimulationEligible(isIntradayMode, from, to, options.batchId, options.lastIntradayBatchId)

  if (!prefix || !modelId || !positionSet || !from || !to) {
    return {
      prefix: prefix || "default",
      ...generateGathererKeys("performance.contributors", { prefix: prefix || "default" }),
      query: "",
      variables: {},
      select: () => ({
        label: "Total",
        values: {},
        children: {},
      }),
    }
  }

  const groupByFields = getGroupByFields(groupByOptions)
  const { groupBy, groupById, tier } = groupByFields
  const sanitizedPositionSet = sanitizePositionSet(positionSet)

  const { key, graphqlKey } = generateGathererKeys("performance.contributors", {
    prefix, modelId, positionSet,
    base,
    from, to,
    groupBy, groupById, tier,
    intraday: shouldUseIntradayQuery || undefined,
    rfr: isRfrAttributionEnabled || undefined,
  })

  return {
    prefix,
    key,
    graphqlKey,
    query: getQuery(groupBy && groupByFields, isRfrAttributionEnabled, shouldUseIntradayQuery),
    variables: {
      modelId,
      positionSet: sanitizedPositionSet,
      base,
      from,
      to,
      ...groupByFields,
      includeNotHeld: !!base,
      ...(shouldUseIntradayQuery && { on: from }),
    },
    select: (result) => {
      const performanceData = last(get(result, "data.model.intradayPerformance.performance")) || get(result, "data.model.simulation.performance.0", {})
      const total = getPerformanceReturns(performanceData, options)

      // Contributors come from the EOD simulation node — an empty intraday total must not drop them.
      const children = groupBy
        ? parseGroupedContributors(prefix, get(result, "data.model.simulation.groupedPerformanceContributors"), options)
        : parseContributors(prefix, get(result, "data.model.simulation.performanceContributors"), options)

      return {
        label: "Total",
        values: {
          [prefix]: total,
        },
        children,
      }
    },
  }
}
